class NewsletterForm extends HTMLElement {
  constructor() {
    super();
    this.onSubmit = this.onSubmit.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    this.requiredFields = this.querySelectorAll("[required]");
    this.submitButton = this.querySelector("[type=submit]");
    this.onlyLettersRegex = /^[A-Za-z\s]+$/;
    this.invalidCharsRegex = /[&@#!$%^*=+<>{}\[\]\/\\|~"()`:;]/;

    this.setBirthdayMaxDate();
    this.submitButton.addEventListener("click", this.onSubmit);
    this.setupInputFiltering();
    this.setupBirthdayValidation();
    this._setupCountries();
  }

  setupInputFiltering() {
    const nameFields = this.form.querySelectorAll(
      'input[name="first_name"], input[name="last_name"], input[name="country"], input[name="state"]',
    );

    nameFields.forEach((field) => {
      field.addEventListener("keydown", (e) => {
        if (this.invalidCharsRegex.test(e.key)) {
          e.preventDefault();
        }
      });
      field.addEventListener("paste", (e) => {
        e.preventDefault();
        const pastedText = (e.clipboardData || window.clipboardData).getData("text");
        const filteredText = pastedText.replace(this.invalidCharsRegex, "");
        const start = field.selectionStart;
        const end = field.selectionEnd;
        const currentValue = field.value;
        field.value = currentValue.slice(0, start) + filteredText + currentValue.slice(end);
        const newPosition = start + filteredText.length;
        field.setSelectionRange(newPosition, newPosition);
      });
    });
  }

  async onSubmit(e) {
    e.preventDefault();
    const formData = new FormData(this.form);
    if (!this.validateFields()) return;
    const payload = new URLSearchParams();
    payload.append("oid", "00D1N000002r6Jz");
    payload.append("retURL", window.location.origin);
    payload.append("email", formData.get("email"));
    payload.append("first_name", formData.get("first_name"));
    payload.append("last_name", formData.get("last_name"));
    if (formData.get("country")) payload.append("country", formData.get("country"));
    if (formData.get("state")) payload.append("state", formData.get("state"));
    const birthday = formData.get("birthday");
    if (birthday) {
      const date = new Date(birthday);
      payload.append("month", date.getMonth() + 1);
      payload.append("day", date.getDate());
      payload.append("birthday__c", `${date.getMonth() + 1}%2F${date.getDate()}`);
    }
    payload.append("sub_type", formData.get("sub_type") || "both");
    payload.append("Subscription_Source__c", "subscribe_page");
    payload.append(
      "Accepts_FP_Newsletter__c",
      formData.get("sub_type") === "newsletter" || formData.get("sub_type") === "both" ? "1" : "0",
    );
    payload.append(
      "Accepts_Blog_Updates__c",
      formData.get("sub_type") === "blog" || formData.get("sub_type") === "both" ? "1" : "0",
    );

    const isUKDomain = window.location.host.includes(".co.uk");
    if (isUKDomain) {
      payload.append("market", "UK");
    }

    try {
      const response = await fetch(formData.get("web-app-domain") + "/newsletter/subscribe", {
        method: "POST",
        body: payload,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      if (response.ok) {
        this.showSuccess("Thank you for subscribing!");
        this.form.reset();
        window.location.href = "/shop";
      } else {
        throw new Error("Submission failed");
      }
    } catch (error) {
      this.showError(this.submitButton, "Something went wrong. Please try again later.", false);
    }
  }

  validateFields() {
    if (!this.requiredFields.length) return true;

    let isValid = true;
    this.clearErrors();

    this.requiredFields.forEach((field) => {
      const fieldName = field.getAttribute("name");
      const label = field.closest(".field")?.querySelector(".field__label");

      if (field.type === "email") {
        if (!EMAIL_VALIDATION_REGEX.test(field.value.trim())) {
          this.showError(field, "Please enter a valid email address");
          if (label) label.classList.add("fp-error-label");
          isValid = false;
        }
      } else if (fieldName === "first_name") {
        if (!field.value.trim() || !this.onlyLettersRegex.test(field.value.trim())) {
          this.showError(field, "Please enter your first name.");
          if (label) label.classList.add("fp-error-label");
          isValid = false;
        }
      } else if (fieldName === "last_name") {
        if (!field.value.trim() || !this.onlyLettersRegex.test(field.value.trim())) {
          this.showError(field, "Please enter your last name.");
          if (label) label.classList.add("fp-error-label");
          isValid = false;
        }
      } else if (!value && field.hasAttribute("required")) {
        this.showError(field, "This field is required");
        if (label) label.classList.add("fp-error-label");
        isValid = false;
      }
    });

    const birthdayField = this.form.querySelector('input[name="birthday"]');
    if (birthdayField) {
      if (!this.validateBirthday(birthdayField)) isValid = false;
    }
    return isValid;
  }

  showError(field, message, icon = true) {
    field.classList.add("fp-field__error");
    const errorContainer = document.createElement("div");
    errorContainer.className = "fp-field__error-container";
    const errorMessage = document.createElement("div");
    errorMessage.className = "fp-field__error-message";
    errorMessage.textContent = message;
    if (icon) {
      const icon = document.createElement("span");
      icon.innerHTML =
        '<svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">\n<path d="M15.8199 12.8874C16.3326 13.7759 15.6891 14.8867 14.665 14.8867H1.33484C0.308813 14.8867 -0.331797 13.7742 0.179925 12.8874L6.84509 1.33283C7.35806 0.443833 8.64287 0.445444 9.15492 1.33283L15.8199 12.8874ZM8 10.4985C7.29431 10.4985 6.72223 11.0705 6.72223 11.7761C6.72223 12.4816 7.29431 13.0536 8 13.0536C8.7057 13.0536 9.27778 12.4816 9.27778 11.7761C9.27778 11.0705 8.7057 10.4985 8 10.4985ZM6.78687 5.90626L6.99292 9.68344C7.00256 9.86019 7.14873 9.99856 7.32575 9.99856H8.67425C8.85128 9.99856 8.99745 9.86019 9.00709 9.68344L9.21314 5.90626C9.22356 5.71534 9.07153 5.55481 8.88031 5.55481H7.11967C6.92845 5.55481 6.77645 5.71534 6.78687 5.90626Z" fill="#B70E0E"/>\n</svg>';
      icon.className = "fp-field__error-icon";
      errorMessage.prepend(icon);
    } else {
      errorMessage.className = "fp-not-subscribed";
    }
    errorContainer.appendChild(errorMessage);
    field.parentNode.appendChild(errorContainer);
  }

  showSuccess(message) {
    const successElement = document.createElement("div");
    successElement.className = "fp-field__success-message";
    successElement.textContent = message;
    this.form.appendChild(successElement);
    setTimeout(() => {
      successElement.remove();
    }, 5000);
  }

  clearErrors() {
    this.requiredFields.forEach((field) => {
      field.classList.remove("fp-field__error");
      const label = field.closest(".field")?.querySelector(".field__label");
      if (label) label.classList.remove("fp-error-label");
      const errorContainer = field.parentNode.querySelector(".fp-field__error-container");
      if (errorContainer) errorContainer.remove();
    });
    const globalError = this.form.querySelector(".fp-global-error");
    if (globalError) globalError.remove();
  }

  validateBirthday(birthdayField) {
    const value = birthdayField.value.trim();
    const label = birthdayField.closest(".field")?.querySelector(".field__label");
    const existingError = birthdayField.parentNode.querySelector(".fp-field__error-container");
    if (existingError) {
      existingError.remove();
      birthdayField.classList.remove("fp-field__error");
      if (label) label.classList.remove("fp-error-label");
    }
    if (!value) return true;
    const [year, month, day] = value.split("-").map(Number);
    const inputDate = new Date(year, month - 1, day);
    inputDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (inputDate > today) {
      this.showError(birthdayField, "Birthday cannot be in the future");
      if (label) label.classList.add("fp-error-label");
      return false;
    }
    return true;
  }

  setBirthdayMaxDate() {
    const birthdayField = this.form.querySelector('input[name="birthday"]');
    if (!birthdayField) return;
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const maxDate = `${yyyy}-${mm}-${dd}`;
    birthdayField.setAttribute("max", maxDate);
  }

  setupBirthdayValidation() {
    const birthdayField = this.form.querySelector('input[name="birthday"]');
    if (!birthdayField) return;
    birthdayField.addEventListener("input", () => {
      this.validateBirthday(birthdayField);
    });
  }

  _setupCountries() {
    if (Shopify && Shopify.CountryProvinceSelector) {
      new Shopify.CountryProvinceSelector("SalesforceCountry", "SalesforceProvince", {
        hideElement: "SalesforceProvinceContainer",
      });
    }
  }
}

customElements.define("salesforce-newsletter", NewsletterForm);
